import { uniqBy } from "lodash";
import { CustomPermittedOffices, IOffice, UserPermissionType, UserRole } from "types";

export interface CombineOfficesOptions {
  baseOffices: IOffice[] | null;
  allOffices: IOffice[] | null;
  customPermittedOffices: CustomPermittedOffices | null;
  permissionType: UserPermissionType | null;
  /** Current user's role */
  userRole?: UserRole | null;
  /** Roles that should only see custom permitted offices (not baseOffices) */
  rolesUsingOnlyCustomPermissions?: UserRole[];
}

/**
 * Combines admin offices with offices from custom permissions for a specific permission type.
 * Merges and deduplicates by office ID.
 *
 * When the user's role is in `rolesUsingOnlyCustomPermissions`, only custom permitted offices
 * are returned (baseOffices are skipped). This is used for analytics pages where certain roles
 * should have restricted access.
 *
 * @param options - Configuration options for combining offices
 * @returns Merged array of offices without duplicates
 */
export function combineOfficesWithPermissions(options: CombineOfficesOptions): IOffice[] {
  const {
    baseOffices,
    allOffices,
    customPermittedOffices,
    permissionType,
    userRole,
    rolesUsingOnlyCustomPermissions = [],
  } = options;

  const offices: IOffice[] = baseOffices ?? [];

  // Check if current user's role should use only custom permissions
  const shouldUseOnlyCustom =
    rolesUsingOnlyCustomPermissions.length > 0 &&
    userRole != null &&
    rolesUsingOnlyCustomPermissions.includes(userRole);

  // If no custom permission for this route
  if (!permissionType || !customPermittedOffices) {
    // For restricted roles without custom permissions, return empty (no access)
    if (shouldUseOnlyCustom) {
      return [];
    }
    return offices;
  }

  // Get custom permitted office IDs for this permission
  const customOfficeIds = customPermittedOffices[permissionType] ?? [];

  // For restricted roles, use ONLY custom role offices
  if (shouldUseOnlyCustom) {
    if (customOfficeIds.length > 0) {
      return allOffices?.filter((office) => customOfficeIds.includes(office.id)) ?? [];
    }
    // No custom permissions for this route - return empty (no access)
    return [];
  }

  // Normal flow: merge base offices with custom role offices
  if (!customOfficeIds.length) {
    return offices;
  }

  // Get offices from custom permission by ID
  const customRoleOffices: IOffice[] = allOffices?.filter((office) => customOfficeIds.includes(office.id)) ?? [];

  // Merge and deduplicate by office ID
  return uniqBy([...offices, ...customRoleOffices], "id");
}
